import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Alert,
  Grid,
  Typography,
  Chip,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  type SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import type { EstadoGuia } from './types';
import { PageTitle, SecondaryActionButton, TextFieldComponent } from '../../../components/common';
import MainSectionPanel from '../../../components/common/MainSectionPanel';
import FormSectionPanel from '../../../components/common/FormSectionPanel';
import DercLookup from '../derc/components/DercLookup';
import ProveedorLookup from '../../catalogo/proveedores/components/ProveedorLookup';
import ArticuloLookup from '../../catalogo/articulos/components/ArticuloLookup';
import { type ArticuloProduccion } from '../../catalogo/articulos/types/articulo';
import { type ArticuloMateriaPrima } from './types';
import ArticulosMateriaPrimaTable from './components/ArticulosMateriaPrimaTable';
import DercInfoDisplay from './components/DercInfoDisplay';
import PresentacionSelectionModal from './components/PresentacionSelectionModal';
import { useGuiaMateriaForm } from './hooks';
import { useGuiaMateriaPrimaStore } from './store/useGuiaMateriaPrimaStore';
import { useDERCStore } from '../derc/store';


const estadoLabels: Record<EstadoGuia, string> = {
  'PEN': 'Pendiente',
  'APR': 'Aprobado',
  'ANU': 'Anulado',
};

const GuiaMateriaFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = id !== 'new';
  const readonlyParam = searchParams.get('readonly') === 'true';

  // State to track if guía has any guía de producción associated (loaded from backend)
  const [tieneGuiaProduccion, setTieneGuiaProduccion] = useState(false);

  // isReadonly is true if URL param is set OR if the guía has guías de producción
  const isReadonly = readonlyParam || tieneGuiaProduccion;

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [presentacionModalOpen, setPresentacionModalOpen] = useState(false);
  const [editingPresentacion, setEditingPresentacion] = useState<{ idPresentacion: number; productos: ArticuloMateriaPrima[] } | null>(null);
  const [initialData, setInitialData] = useState<any>(null);
  const [, setLoadingGuia] = useState(false);
  const [detectedProductType, setDetectedProductType] = useState<'A' | 'B' | null>(null);
  const [estado, setEstado] = useState<EstadoGuia>('PEN');
  const dercIdFromUrl = searchParams.get('dercId');
  const productTypeFromUrl = searchParams.get('productType');
  const { createGuia, fetchGuiaById, updateGuia, updateEstado, loading: storeLoading } = useGuiaMateriaPrimaStore();
  const { getDERCById } = useDERCStore();

  // Determine effective product type: from URL for new, or detected from data for edit
  const effectiveProductType = productTypeFromUrl || detectedProductType;

  const handleEstadoChange = async (event: SelectChangeEvent) => {
    const newEstado = event.target.value as EstadoGuia;
    if (isEdit && id) {
      const success = await updateEstado(parseInt(id), newEstado);
      if (success) {
        setEstado(newEstado);
        setAlertMessage(`Estado actualizado a "${estadoLabels[newEstado]}" exitosamente.`);

        // Redirect to list when approved
        if (newEstado === 'APR') {
          setTimeout(() => {
            navigate('/produccion/guias-materia-prima');
          }, 1500);
        } else {
          setTimeout(() => setAlertMessage(null), 3000);
        }
      } else {
        setAlertMessage('Error al actualizar el estado.');
        setTimeout(() => setAlertMessage(null), 5000);
      }
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      console.log('Guardar request body:', data);
      let result;
      if (isEdit && id) {
        result = await updateGuia(parseInt(id), data);
      } else {
        result = await createGuia(data);
      }
      if (result) {
        console.log('Guía guardada exitosamente:', result);
        setAlertMessage('Guía de materia prima guardada exitosamente');
        setTimeout(() => {
          setAlertMessage(null);
          navigate('/produccion/guias-materia-prima');
        }, 2000);
      } else {
        setAlertMessage('Error al guardar la guía de materia prima');
      }
    } catch (error) {
      console.error('Error al guardar la guía:', error);
      setAlertMessage('Error al guardar la guía de materia prima');
    }
  };

  const {
    formData,
    productos,
    errors,
    isSubmitting,
    handleDercChange,
    handleProveedorChange,
    handleSwitchChange: _handleSwitchChange,
    handleInputChange,
    handleCantidadChange,
    handleDuenioChange,
    handleEncargadoChange,
    handleRemoveProducto,
    addProducto,
    addMultipleProductos,
    handleAddProducto,
    handleSubmit: onSubmit,
  } = useGuiaMateriaForm({
    onSubmit: handleSubmit,
    isOpen: true,
    productType: effectiveProductType as 'A' | 'B' | null,
    initialData: initialData,
  });

  // Load guia data when in edit mode
  useEffect(() => {
    const loadGuiaData = async () => {
      if (isEdit && id && id !== 'new') {
        setLoadingGuia(true);
        try {
          const guia = await fetchGuiaById(parseInt(id));
          console.log('Guía cargada:', guia);
          if (guia) {
            // Transform guia data to initialData format
            const transformedData = {
              id: guia.id,
              formData: {
                selectedDercId: guia.idDocumento || null,
                selectedDercNumber: guia.documentoNumero || '',
                selectedProveedorId: guia.idProveedor || null,
                selectedProveedorName: guia.proveedorRazonSocial || '',
                esServicio: guia.isServicio || false,
                observaciones: guia.observaciones || '',
                notas: guia.notas || '',
                dercInfo: guia.idDocumento ? {
                  numeroDocumento: guia.documentoNumero || '',
                  fechaExtraccion: guia.fechaExtraccion || '',
                  asociacionDescripcion: guia.asociacion || '',
                  numeroMallas: guia.numeroMallas || 0,
                  numeroManojos: guia.numeroManojos || 0,
                  placaVehiculo: guia.placaVehiculo || '',
                  idEspecie: guia.idEspecie || 0,
                  especieDescripcion: guia.especieDescripcion || '',
                  isEntero: guia.isEntero || false,
                } : null,
              },
              productos: (guia.detalles || []).map((detalle: any, index: number) => ({
                id: Date.now() + index,
                detalleId: detalle.id,
                idArticulo: detalle.idArticulo,
                codigoArticulo: detalle.articuloCodigo || '',
                descripcion: detalle.articuloDescripcion || detalle.descripcion || '',
                especieNombreComun: detalle.especieNombreComun || 'N/A',
                coeficiente: detalle.coeficiente || 0,
                pesoEmpaqueInterior: detalle.pesoEmpaqueInterior || detalle.coeficiente || 0,
                cantidad: detalle.cantidad || 0,
                total: (detalle.pesoEmpaqueInterior || detalle.coeficiente || 0) * (detalle.cantidad || 0),
                duenio: detalle.idDuenio || '',
                encargado: detalle.idEncargado || '',
                calibre: detalle.calibre || 'Sin Calibre',
                idPresentacion: detalle.idPresentacion || null,
                presentacion: detalle.presentacionDescripcion || '',
              })),
            };
            console.log('Transformed data:', transformedData);
            setInitialData(transformedData);

            // Set estado from loaded guia
            if (guia.estado) {
              setEstado(guia.estado as EstadoGuia);
            }

            // Check if guía has any guía de producción associated (makes it readonly)
            if (guia.tieneGuiaProduccion) {
              setTieneGuiaProduccion(true);
            }

            // Detect product type based on IdOrigenProducto from Documento
            // IdOrigenProducto: 1 = Type A, 2 = Type B
            if (guia.idOrigenProducto === 2) {
              setDetectedProductType('B');
            } else {
              setDetectedProductType('A');
            }
          }
        } catch (error) {
          console.error('Error loading guia:', error);
          setAlertMessage('Error al cargar la guía de materia prima');
        } finally {
          setLoadingGuia(false);
        }
      }
    };

    loadGuiaData();
  }, [isEdit, id, fetchGuiaById]);

  // Auto-select DERC if dercId is provided in URL
  useEffect(() => {
    const loadDercFromUrl = async () => {
      if (dercIdFromUrl && !formData.selectedDercId) {
        try {
          const derc = await getDERCById(parseInt(dercIdFromUrl));

          if (derc && derc.numeroDocumento) {
            handleDercChange(parseInt(dercIdFromUrl), derc.numeroDocumento, derc);
            const productTypeText = productTypeFromUrl ? ` (Producto ${productTypeFromUrl})` : '';
            setAlertMessage(`DERC ${derc.numeroDocumento}${productTypeText} seleccionado automáticamente.`);
            setTimeout(() => setAlertMessage(null), 3000);
          }
        } catch (error) {
          console.error('Error loading DERC from URL:', error);
          setAlertMessage('Error al cargar el DERC seleccionado. Por favor, selecciónelo manualmente.');
          setTimeout(() => setAlertMessage(null), 5000);
        }
      }
    };

    loadDercFromUrl();
  }, [dercIdFromUrl, formData.selectedDercId, handleDercChange, getDERCById]);

  // Show message when default product is loaded
  useEffect(() => {
    if (productTypeFromUrl && productos.length > 0) {
      // Check if any product was automatically loaded (assumes first product is the default)
      const firstProduct = productos[0];
      if (firstProduct && firstProduct.cantidad === 0) {
        setAlertMessage(`Producto por defecto agregado para Tipo ${productTypeFromUrl}: ${firstProduct.descripcion}`);
        setTimeout(() => setAlertMessage(null), 4000);
      }
    }
  }, [productos, productTypeFromUrl]);

  const handleVolver = () => {
    navigate('/produccion/guias-materia-prima');
  };

  const handleArticuloSelect = (articulo: ArticuloProduccion | null) => {
    console.log('Artículo seleccionado:', articulo);

    // Handle null or error cases
    if (!articulo) {
      setAlertMessage('No se pudo obtener la información del artículo seleccionado.');
      setTimeout(() => setAlertMessage(null), 5000);
      return;
    }

    // Check if articulo is already added
    const existingProduct = productos.find(p => p.idArticulo === articulo.idArticulo);
    if (existingProduct) {
      setAlertMessage(`El artículo "${articulo.descripcion}" ya está agregado a la lista.`);
      setTimeout(() => setAlertMessage(null), 5000);
      return;
    }

    // Add new product to table using production articulo object from lookup
    // For Type A or Type B "entero", use species description as presentation grouping
    const pesoEmpaqueInterior = (articulo as any).pesoEmpaqueInterior || articulo.coeficiente;
    const presentacionDefault = formData.dercInfo?.especieDescripcion || articulo.especieNombreComun || 'Sin Presentación';

    const newProduct: ArticuloMateriaPrima = {
      id: Date.now(),
      idArticulo: articulo.idArticulo,
      codigoArticulo: articulo.codigoArticulo,
      descripcion: articulo.descripcion,
      especieNombreComun: articulo.especieNombreComun || 'N/A',
      coeficiente: articulo.coeficiente,
      pesoEmpaqueInterior,
      cantidad: 0,
      total: 0, // Initialize total as 0 since cantidad is 0
      duenio: '',
      encargado: '',
      calibre: articulo.calibre || 'Sin Calibre',
      presentacion: presentacionDefault, // Group products by species/presentation
    };

    addProducto(newProduct);
    setAlertMessage(`Artículo "${articulo.descripcion}" agregado exitosamente.`);
    setTimeout(() => setAlertMessage(null), 3000);
  };

  const handleBuscarProductoClick = () => {
    // Check if we have DERC info with species for product type B (non-entero)
    // Product type B "entero" should behave like product type A
    const isProductBNotEntero = effectiveProductType === 'B' && !formData.dercInfo?.isEntero;
    if (isProductBNotEntero && formData.dercInfo?.idEspecie) {
      setPresentacionModalOpen(true);
    } else {
      // For product type A, product type B "entero", or when no species is available, use the regular lookup
      // This will be handled by the ArticuloLookup component directly
      console.log('Using regular article lookup for product type A, product type B "entero", or no species available');
    }
  };

  const handleEditPresentacion = (idPresentacion: number, productosGrupo: ArticuloMateriaPrima[]) => {
    console.log('Editar presentación:', idPresentacion, productosGrupo);
    setEditingPresentacion({ idPresentacion, productos: productosGrupo });
    setPresentacionModalOpen(true);
  };

  const handlePresentacionSelectProducts = (selectedProducts: ArticuloMateriaPrima[]) => {
    console.log('Selected products from presentacion modal:', selectedProducts);

    // Si estamos en modo edición, reemplazar los productos de esa presentación
    if (editingPresentacion) {
      // Eliminar todos los productos de la presentación que se está editando
      // Actualizar todos los productos
      // Eliminar los antiguos productos de esta presentación y agregar los nuevos
      editingPresentacion.productos.forEach(p => handleRemoveProducto(p.id));
      addMultipleProductos(selectedProducts);

      setAlertMessage('Presentación actualizada exitosamente.');
      setEditingPresentacion(null);
      setTimeout(() => setAlertMessage(null), 3000);
      return;
    }

    // Check for duplicates and filter out existing products
    const newProducts: ArticuloMateriaPrima[] = [];
    let duplicateCount = 0;

    selectedProducts.forEach(product => {
      const existingProduct = productos.find(p => p.idArticulo === product.idArticulo);
      if (!existingProduct) {
        newProducts.push(product);
      } else {
        duplicateCount++;
      }
    });

    // Add new products using the multiple products function
    if (newProducts.length > 0) {
      addMultipleProductos(newProducts);
    }

    // Show feedback message
    if (newProducts.length > 0) {
      let message = `${newProducts.length} productos agregados exitosamente.`;
      if (duplicateCount > 0) {
        message += ` ${duplicateCount} productos ya estaban en la lista.`;
      }
      setAlertMessage(message);
    } else if (duplicateCount > 0) {
      setAlertMessage('Todos los productos seleccionados ya están en la lista.');
    }

    setTimeout(() => setAlertMessage(null), 4000);
    setPresentacionModalOpen(false);
  };


  return (
    <MainSectionPanel>
      <PageTitle
        title={isReadonly ? 'Ver Guía de Materia Prima' : (isEdit ? 'Editar Guía de Materia Prima' : 'Nueva Guía de Materia Prima')}
        subtitle="Gestión de guías de materia prima"
        actions={
          <SecondaryActionButton
            onClick={handleVolver}
            startIcon={<ArrowBackIcon />}
            label="Volver"
          />
        }
      />

      {/* Product Type and Estado Indicators */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {effectiveProductType && (
          <Chip
            label={`Tipo de Producto: ${effectiveProductType}`}
            color={effectiveProductType === 'A' ? 'primary' : 'error'}
            size="medium"
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        )}

        {/* Estado selector - only show in edit mode */}
        {isEdit && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Estado:</Typography>
            {tieneGuiaProduccion ? (
              <Chip
                label="Con Guía Producción"
                color="info"
                size="small"
              />
            ) : isReadonly ? (
              <Chip
                label={estadoLabels[estado]}
                color={estado === 'PEN' ? 'warning' : estado === 'APR' ? 'success' : 'error'}
                size="small"
              />
            ) : (
              <Select
                value={estado}
                onChange={handleEstadoChange}
                size="small"
                disabled={storeLoading}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="PEN">
                  <Chip label="Pendiente" color="warning" size="small" />
                </MenuItem>
                <MenuItem value="APR">
                  <Chip label="Aprobado" color="success" size="small" />
                </MenuItem>
                <MenuItem value="ANU">
                  <Chip label="Anulado" color="error" size="small" />
                </MenuItem>
              </Select>
            )}
          </Box>
        )}
      </Box>

      {/* Main Form Section */}
      <FormSectionPanel title="Información General">
        <Grid container spacing={3}>
          {/* First Row - DERC */}
          <Grid size={{ xs: 12, md: 12 }}>
            <DercLookup
              label="DERC de Referencia"
              value={formData.selectedDercNumber}
              onChange={isReadonly ? undefined : handleDercChange}
              placeholder="Seleccione un DERC"
              modalTitle="Seleccionar DERC"
              required
              error={errors.selectedDercId}
              showInfoPanel={false}
              disabled={isReadonly}
            />
            <DercInfoDisplay dercInfo={formData.dercInfo} />
          </Grid>

          {/* Second Row - Proveedor */}
          <Grid size={{ xs: 12, md: 12 }}>
            <ProveedorLookup
              label="Proveedor"
              value={formData.selectedProveedorName}
              onChange={isReadonly ? undefined : handleProveedorChange}
              placeholder="Seleccione un proveedor"
              modalTitle="Seleccionar Proveedor"
              required
              error={errors.selectedProveedorId}
              disabled={isReadonly}
            />
          </Grid>
        </Grid>
      </FormSectionPanel>

      {/* Products Section */}
      {/* Product type B "entero" should behave like product type A for product selection */}
      <FormSectionPanel
        title="Sub Lotes"
        actions={
          isReadonly ? undefined : (
            // Show Presentacion modal button only for Product B that is NOT "entero"
            effectiveProductType === 'B' && !formData.dercInfo?.isEntero && formData.dercInfo?.idEspecie ? (
              <Button
                variant="outlined"
                size="small"
                onClick={handleBuscarProductoClick}
              >
                Buscar Producto
              </Button>
            ) : (
              // For Product A or Product B "entero", use regular ArticuloLookup
              <ArticuloLookup
                onSelect={handleArticuloSelect}
                buttonText="Buscar Producto"
                modalTitle="Seleccionar Producto para Agregar"
                variant="outlined"
                size="small"
              />
            )
          )
        }
      >
        {/* Products validation error */}
        {errors.productos && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {errors.productos}
          </Typography>
        )}
        {/* Alert Messages */}
        {alertMessage && (
          <Alert
            severity={alertMessage.includes('agregado') ? 'success' : alertMessage.includes('eliminado') ? 'info' : 'warning'}
            sx={{ mb: 2 }}
            onClose={() => setAlertMessage(null)}
          >
            {alertMessage}
          </Alert>
        )}

        <ArticulosMateriaPrimaTable
          articulos={productos}
          onCantidadChange={isReadonly ? undefined : handleCantidadChange}
          onDuenioChange={isReadonly ? undefined : handleDuenioChange}
          onEncargadoChange={isReadonly ? undefined : handleEncargadoChange}
          onRemoveArticulo={isReadonly ? undefined : handleRemoveProducto}
          onAddArticulo={isReadonly ? undefined : async () => {
            const result = await handleAddProducto();
            if (result === 'openPresentacionModal') {
              setPresentacionModalOpen(true);
            }
          }}
          onEditPresentacion={isReadonly ? undefined : handleEditPresentacion}
          numeroMallas={formData.dercInfo?.numeroMallas || 0}
          readonly={isReadonly}
        />
      </FormSectionPanel>

      {/* Observaciones Section */}
      <FormSectionPanel title="Observaciones">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextFieldComponent
              label="Observaciones"
              value={formData.observaciones}
              onChange={handleInputChange('observaciones')}
              placeholder="Ingrese observaciones..."
              fullWidth
              multiline
              rows={4}
              disabled={isReadonly}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextFieldComponent
              label="Notas"
              value={formData.notas}
              onChange={handleInputChange('notas')}
              placeholder="Ingrese notas adicionales..."
              fullWidth
              multiline
              rows={4}
              disabled={isReadonly}
            />
          </Grid>
        </Grid>
      </FormSectionPanel>

      {/* Bottom Action Bar */}
      {!isReadonly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={handleVolver} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>
      )}

      {/* Presentacion Selection Modal for Product Type B */}
      <PresentacionSelectionModal
        open={presentacionModalOpen}
        onClose={() => {
          setPresentacionModalOpen(false);
          setEditingPresentacion(null);
        }}
        onSelectProducts={handlePresentacionSelectProducts}
        idEspecie={formData.dercInfo?.idEspecie || 0}
        especieNombre={formData.dercInfo?.especieDescripcion || 'N/A'}
        editingProducts={editingPresentacion?.productos}
      />
    </MainSectionPanel>
  );
};

export default GuiaMateriaFormPage;